// ============================================
// document-table-shortcut.ts — R20 `+table=3x4` shortcut
// ============================================
//
// A small TipTap extension that watches for the user typing a
// `+table=rows x cols` pattern at the END of an empty paragraph and
// converts it to a freshly inserted table on the fly.
//
// R20: the shortcut syntax changed from bare `3x4` to the explicit
// `+table=3x4` form so it never fires on prose mentions of
// dimensions like "3x4 profit" or "5*2 matrix" — those would
// otherwise auto-insert a table and confuse the user. The user
// confirmed the explicit `+table=` prefix during the R20 design
// review.
//
// Patterns accepted (case-insensitive, separators flexible):
//
//   +table=3x4       +table=3 x 4     +TABLE=3X4
//   +table=3*4       + table = 10 x 10
//
// The match fires only when the user types a TRIGGERING WHITESPACE
// character (space, tab, enter) right after the second number. We
// intentionally do NOT match `+table=3x4` mid-word (e.g.
// `pre+table=3x4` does nothing) — that would be confusing for
// normal prose.
//
// Bounds: rows/cols are clamped to [1, 20] to keep the document
// responsive (TipTap's default `insertTable` accepts any positive
// integer; a 1000x1000 table would freeze the page). Out-of-range
// values are silently ignored — no error, no crash.
//
// The extension is a thin wrapper around `@tiptap/core`'s
// `InputRule` API. We keep the validation + insertion logic in a
// single `handler` so future rules (e.g. `+table=1x1` → just a
// single cell) can plug in cleanly.

import { Extension, InputRule } from '@tiptap/core';

// Maximum rows / columns we accept. TipTap has no built-in cap and
// a 1000x1000 table would freeze the editor + browser.
const MAX_DIMENSION = 20;
const MIN_DIMENSION = 1;

/**
 * Match a `+table=rows x cols` pattern immediately followed by
 * whitespace (space, tab, newline). Anchored to the end of the
 * matched text so we don't fire on `pre+table=3x4` mid-sentence.
 *
 * Capture groups:
 *   1: rows
 *   2: separator (x / X / *)
 *   3: cols
 *   4: trailing whitespace (single char trigger)
 */
const TABLE_SHORTCUT_REGEX = /\+table\s*=\s*(\d+)\s*[xX*]\s*(\d+)\s$/;

/**
 * Build the input rule. We split this into a function so the regex
 * is documented + unit-testable without instantiating the full
 * extension.
 */
function buildTableShortcutInputRule(): InputRule {
    return new InputRule({
        find: TABLE_SHORTCUT_REGEX,
        handler: ({ range, match, chain }) => {
            const rowsRaw = match[1];
            const colsRaw = match[2];
            if (!rowsRaw || !colsRaw) return;

            const rows = parseInt(rowsRaw, 10);
            const cols = parseInt(colsRaw, 10);

            // Out-of-range: silently ignore. The user typed something
            // we don't recognise as a table shortcut — the prose
            // stays untouched and the regex simply won't fire next
            // time.
            if (
                !Number.isFinite(rows) ||
                !Number.isFinite(cols) ||
                rows < MIN_DIMENSION ||
                rows > MAX_DIMENSION ||
                cols < MIN_DIMENSION ||
                cols > MAX_DIMENSION
            ) {
                return;
            }

            // The regex captures the leading whitespace + the entire
            // "rows x cols " token; `range` spans that token. We
            //   1. delete that token
            //   2. insert a rows x cols table (with header row)
            // The chain stays in focus so the cursor lands inside
            // the first cell after the insert.
            chain()
                .focus()
                .deleteRange({ from: range.from, to: range.to })
                .insertTable({ rows, cols, withHeaderRow: true })
                .run();
        },
    });
}

export interface DocumentTableShortcutOptions {
    /**
     * When true (default), the extension is mounted. Set to false
     * from `DocumentEditor` if a future round needs to disable
     * the shortcut (e.g. during read-only mode or when the user
     * is inside a table cell).
     */
    enabled?: boolean;
}

/**
 * TipTap extension that converts a typed `rows x cols` shortcut
 * into a freshly inserted table.
 *
 * Usage:
 *
 *   const editor = useEditor({
 *     extensions: [
 *       StarterKit.configure({ history: false }),
 *       // ... other extensions ...
 *       DocumentTableShortcut,
 *     ],
 *   });
 */
export const DocumentTableShortcut = Extension.create<DocumentTableShortcutOptions>({
    name: 'documentTableShortcut',

    addOptions() {
        return {
            enabled: true,
        };
    },

    addInputRules() {
        if (this.options.enabled === false) return [];
        return [buildTableShortcutInputRule()];
    },
});

/**
 * Pure helper exported for tests + future callers (e.g. the toolbar
 * "insert table" button could validate user-typed dimensions with
 * the same bounds check). Returns null when the input is invalid.
 */
export function parseTableShortcut(
    text: string
): { rows: number; cols: number } | null {
    const match = TABLE_SHORTCUT_REGEX.exec(text);
    if (!match) return null;
    const rowsRaw = match[1];
    const colsRaw = match[2];
    if (!rowsRaw || !colsRaw) return null;
    const rows = parseInt(rowsRaw, 10);
    const cols = parseInt(colsRaw, 10);
    if (
        !Number.isFinite(rows) ||
        !Number.isFinite(cols) ||
        rows < MIN_DIMENSION ||
        rows > MAX_DIMENSION ||
        cols < MIN_DIMENSION ||
        cols > MAX_DIMENSION
    ) {
        return null;
    }
    return { rows, cols };
}

export const DOCUMENT_TABLE_SHORTCUT_MAX_DIMENSION = MAX_DIMENSION;
export const DOCUMENT_TABLE_SHORTCUT_MIN_DIMENSION = MIN_DIMENSION;
